import React, { Component } from 'react';
import { Drawer } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { BellIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
    Breadcrumb,
    ButtonSizes,
    IconButton,
    Popover,
    PopoverTriggers,
    PopoverPlacements,
} from '@clockout-dev/design-elements';
import Inherit from '../../../core/inherit';
import withHooks from '../../../core/withHooks';
import HeaderController from './header.controller';
import styles from './styles/header.module.scss';
import NewSidebar from './NewSidebar';
import NotificationsMenu from './elements/NotificationsMenu';
import AccountMenu from './elements/AccountMenu';
import HeadphonesIcon from '../headphonesIcon/HeadphonesIcon';
import menuStyles from './styles/notificationsMenu.module.scss';

/**
 * Admin 3.0 «Navbar» (Figma 797:54130): breadcrumb on the left; Notifications,
 * Support and the account menu on the right. Tablet / mobile add the menu
 * button that opens the sidebar in a drawer.
 */
class Header extends Inherit(HeaderController, Component) {
    render() {
        const { drawerOpen, notificationsOpen, menuItems, menuLoading, menuError, unreadCount } = this.state;
        const hasNotifications = this.hasNotifications();
        const steps = this.getBreadcrumbSteps();
        const navButtonProps = { size: ButtonSizes.COMPACT, classNames: { icon: styles.navIcon } };
        const showActions = this.shouldShowNotifications() || this.shouldShowSupport();

        return (
            <header className={styles.header}>
                <button className={styles.menuButton} onClick={this.handleMenuClick.bind(this)} aria-label="Menu">
                    <MenuOutlined />
                </button>
                {steps.length ? (
                    <Breadcrumb
                        steps={steps}
                        onNavigate={(path) => this.handleBreadcrumbNavigate(path)}
                        className={styles.breadcrumb}
                        classNames={{ current: styles.breadcrumbCurrent }}
                    />
                ) : null}
                <div className={styles.headerRight}>
                    {showActions ? (
                        <div className={styles.headerActions}>
                            {this.shouldShowNotifications() ? (
                                <Popover
                                    trigger={PopoverTriggers.CLICK}
                                    placement={PopoverPlacements.BOTTOM_RIGHT}
                                    arrow={false}
                                    open={notificationsOpen}
                                    onOpenChange={(open) => this.handleNotificationsOpenChange(open)}
                                    classNames={{ root: menuStyles.popoverRoot }}
                                    content={
                                        <NotificationsMenu
                                            items={menuItems}
                                            loading={menuLoading}
                                            error={menuError}
                                            unreadCount={unreadCount}
                                            onItemClick={(item) => this.handleMenuItemClick(item)}
                                            onViewAll={() => this.handleViewAllClick()}
                                        />
                                    }
                                >
                                    <IconButton
                                        {...navButtonProps}
                                        indicator={hasNotifications}
                                        icon={<BellIcon />}
                                        aria-label={this.getNotificationsLabel()}
                                        aria-haspopup="menu"
                                        aria-expanded={notificationsOpen}
                                    />
                                </Popover>
                            ) : null}
                            {this.shouldShowSupport() ? (
                                <IconButton
                                    {...navButtonProps}
                                    icon={<HeadphonesIcon />}
                                    aria-label="Support"
                                    onClick={this.handleSupportClick.bind(this)}
                                />
                            ) : null}
                        </div>
                    ) : null}
                    <AccountMenu
                        user={this.getHeaderUser()}
                        fi={this.getHeaderFi()}
                        role={this.getAccountRole()}
                        onLogout={this.handleLogoutClick}
                    />
                </div>

                <Drawer
                    placement="left"
                    onClose={this.handleDrawerClose.bind(this)}
                    open={drawerOpen}
                    className={styles.mobileDrawer}
                    width={280}
                    closable={false}
                    styles={{ header: { display: 'none' }, body: { padding: 0 } }}
                >
                    <div className={styles.drawerCloseBar}>
                        <button
                            type="button"
                            className={styles.drawerCloseButton}
                            onClick={this.handleDrawerClose.bind(this)}
                            aria-label="Close menu"
                        >
                            <XMarkIcon className={styles.drawerCloseIcon} aria-hidden />
                        </button>
                    </div>
                    <NewSidebar
                        user={this.props.user}
                        fi={this.props.fi}
                        collapsible={false}
                        classNames={{ root: styles.drawerNav, panel: styles.drawerPanel }}
                    />
                </Drawer>
            </header>
        );
    }
}

export default withHooks(Header);
